import { Controller } from "@hotwired/stimulus"

// E-mail capture pop-up on the Cosy Fantasy landing, per the v4 mockup: it never interrupts
// a fresh arrival — the visitor must have been on the page for at least MIN_DWELL — and then
// opens on whichever comes first, reading most of the page or moving to leave. Shown once per
// session, so a second visit in the same tab is left alone.
// Classic JS only (old Babel — no ?./??).
// Minimum time on the page before the pop-up may appear, and how far down the visitor must
// have read for the scroll trigger to fire.
const MIN_DWELL = 30000
const SCROLL_RATIO = 0.75

export default class extends Controller {
  static targets = ["dialog"]
  static values = { storageKey: { type: String, default: "cosy-popup-seen" } }

  connect() {
    if (!this.hasDialogTarget) return

    // Subscribing before the alreadySeen() bail-out on purpose: a visitor who signs up
    // through the inline form must still have the flag written, so the pop-up stays away
    // for the rest of the session (QA, 28/08). The listener sits on the controller's own
    // element, which wraps both copies of the form.
    this.onSubmitEnd = (event) => this.rememberOnLeadSuccess(event)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)

    if (this.alreadySeen()) return

    this.openedAt = new Date().getTime()
    this.done = false

    this.onScroll = () => this.checkScroll()
    this.onLeave = (event) => this.checkExitIntent(event)
    this.onKeydown = (event) => this.checkEscape(event)

    window.addEventListener("scroll", this.onScroll, { passive: true })
    document.addEventListener("mouseout", this.onLeave)
  }

  disconnect() {
    this.teardownTriggers()
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown)
    if (this.onSubmitEnd) this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  // Any opt-in form on the page (hero, inline section, pop-up) counts as "already
  // subscribed". A validation failure re-renders the form with a 422, and Turbo reports
  // that as success === false, so a typo does not silence the pop-up.
  rememberOnLeadSuccess(event) {
    const form = event.target
    if (!form || !form.classList || !form.classList.contains("cosy-optin-form")) return
    if (!event.detail || !event.detail.success) return

    this.remember()
    this.done = true
    this.teardownTriggers()
  }

  checkScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    if (scrollable <= 0) return
    if (window.scrollY / scrollable > SCROLL_RATIO) this.open()
  }

  // clientY <= 0 means the cursor left through the top of the viewport — heading for the
  // tab bar or the close button, which is the moment worth interrupting.
  checkExitIntent(event) {
    if (event.clientY <= 0) this.open()
  }

  checkEscape(event) {
    if (event.key === "Escape") this.close()
  }

  open() {
    if (this.done) return
    if (new Date().getTime() - this.openedAt < MIN_DWELL) return

    this.done = true
    this.remember()
    this.teardownTriggers()

    this.dialogTarget.hidden = false
    this.dialogTarget.classList.add("is-open")

    // Return focus where it came from once the dialog closes, and let Escape dismiss it —
    // neither is in the mockup, but a modal without them traps keyboard users.
    this.previouslyFocused = document.activeElement
    document.addEventListener("keydown", this.onKeydown)

    const field = this.dialogTarget.querySelector("input:not([type=hidden])")
    if (field) field.focus()
  }

  close() {
    this.dialogTarget.classList.remove("is-open")
    this.dialogTarget.hidden = true
    document.removeEventListener("keydown", this.onKeydown)

    if (this.previouslyFocused && this.previouslyFocused.focus) this.previouslyFocused.focus()
  }

  teardownTriggers() {
    if (this.onScroll) window.removeEventListener("scroll", this.onScroll)
    if (this.onLeave) document.removeEventListener("mouseout", this.onLeave)
  }

  // Safari in private mode throws on storage access rather than returning null, so every
  // read and write is guarded; a failure just means the pop-up may show again next visit.
  alreadySeen() {
    try {
      return sessionStorage.getItem(this.storageKeyValue) === "1"
    } catch (error) {
      return false
    }
  }

  remember() {
    try {
      sessionStorage.setItem(this.storageKeyValue, "1")
    } catch (error) {
      // Nothing to do — the pop-up simply is not remembered.
    }
  }
}
